/**
 * Two read-only sections of the player card: PROTECTION (ADR-070) and the
 * player's own EVENT HISTORY (ADR-068).
 *
 * Kept out of `PlayerCard.tsx` so the derivation below is a pure function of a
 * list of events, testable without a component, and so the card file stays a
 * layout rather than a grab-bag.
 *
 * ── A limitation stated plainly, because hiding it would be worse ──
 *
 * The protective state itself (`player_protection`) is exposed only to the
 * PLAYER, at `GET /me/limits`. There is no operator read of it: no
 * `/internal/admin/players/:id/protection`, and no route listing currently
 * excluded accounts. The panel authenticates as an operator and cannot call a
 * `/me/*` route on someone else's behalf — so this section reconstructs the
 * state from the player's own audit log, which records every change to it, plus
 * the birthdate and country the admin card already carries.
 *
 * That reconstruction is honest but not authoritative, and the UI says so:
 *
 *   · It sees only the last page of events (200 rows). An older change is
 *     invisible, which is why "no recorded change" is never rendered as
 *     "no limit".
 *   · A pending increase is promoted silently by the server when its 24 hours
 *     elapse — there is no event for the promotion — so an increase whose time
 *     has passed is shown as "due or already in force", not as pending.
 *
 * Everything here is READ-ONLY, and not for want of effort. An operator cannot
 * shorten a self-exclusion or raise a limit because the server has no code path
 * that does either: `selfExclude` refuses any end earlier than the one in
 * force, and a limit increase always waits out its cooldown. A button that
 * would 4xx on every click is worse than no button — it teaches the operator
 * that the panel is broken rather than that the control is deliberate.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  fetchPlayerEvents,
  type PlayerEventView,
  type PlayerProfile,
} from '../api/support.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age, shortTime } from '../lib/datetime.ts';
import { format } from '../lib/money.ts';
import { Badge, Card, EmptyState, ErrorState, TableSkeleton } from '../ui/primitives.tsx';
import { useToast } from '../ui/feedback.tsx';
import { IconAlert, IconPulse, IconShield } from '../ui/icons.tsx';

/** How far back the reconstruction can see. The server's own per-page ceiling. */
const SCAN_LIMIT = 200;

/** The statutory minimum age the server enforces at registration and login. */
const MIN_AGE_YEARS = 18;

// ------------------------------------------------------------ pure functions

export interface LimitTriple {
  daily: string | null;
  weekly: string | null;
  monthly: string | null;
}

export interface ProtectionSnapshot {
  /** The most recent limit DECREASE (an increase is scheduled, not applied). */
  limits: { eventId: number; at: string; windows: string[]; set: LimitTriple } | null;
  pending: {
    eventId: number;
    at: string;
    windows: string[];
    set: LimitTriple;
    effectiveAt: string | null;
  } | null;
  exclusion: {
    at: string;
    permanent: boolean;
    until: string | null;
    extended: boolean;
  } | null;
  terms: { at: string; version: string; source: string | null } | null;
  /** What the account declared at sign-up, when the account is new enough to have it. */
  declared: {
    at: string;
    birthdate: string | null;
    countryCode: string | null;
    termsVersion: string | null;
  } | null;
  /** True when the scan filled its page, so an older change may exist unseen. */
  truncated: boolean;
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function words(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** A limit is a decimal string or an explicit null. Anything else is not a limit. */
function tripleOf(value: unknown): LimitTriple {
  const row = record(value);
  const one = (key: string): string | null => {
    const v = row?.[key];
    return typeof v === 'string' && /^\d+(\.\d+)?$/.test(v) ? v : null;
  };
  return { daily: one('daily'), weekly: one('weekly'), monthly: one('monthly') };
}

/**
 * Reconstruct the protective state from the player's own history.
 *
 * `events` must be newest-first, which is the order both `/me/events` and the
 * admin route return. The first match of each type therefore wins.
 */
export function deriveProtection(events: PlayerEventView[]): ProtectionSnapshot {
  const snapshot: ProtectionSnapshot = {
    limits: null,
    pending: null,
    exclusion: null,
    terms: null,
    declared: null,
    truncated: events.length >= SCAN_LIMIT,
  };

  for (const event of events) {
    const detail = record(event.detail);
    switch (event.type) {
      case 'limit_changed':
        if (!snapshot.limits) {
          snapshot.limits = {
            eventId: event.id,
            at: event.createdAt,
            windows: words(detail?.['windows']),
            set: tripleOf(detail?.['limits']),
          };
        }
        break;
      case 'limit_increase_scheduled':
        if (!snapshot.pending) {
          snapshot.pending = {
            eventId: event.id,
            at: event.createdAt,
            windows: words(detail?.['windows']),
            set: tripleOf(detail?.['pending']),
            effectiveAt: text(detail?.['effectiveAt']),
          };
        }
        break;
      case 'self_exclusion_changed':
        if (!snapshot.exclusion) {
          snapshot.exclusion = {
            at: event.createdAt,
            permanent: detail?.['permanent'] === true,
            until: text(detail?.['until']),
            extended: detail?.['extended'] === true,
          };
        }
        break;
      case 'terms_accepted':
        if (!snapshot.terms) {
          snapshot.terms = {
            at: event.createdAt,
            version: text(detail?.['version']) ?? 'unknown',
            source: text(detail?.['source']),
          };
        }
        break;
      case 'registration_declared':
        if (!snapshot.declared) {
          snapshot.declared = {
            at: event.createdAt,
            birthdate: text(detail?.['birthdate']),
            countryCode: text(detail?.['countryCode']),
            termsVersion: text(detail?.['termsVersion']),
          };
        }
        break;
      default:
        break;
    }
  }

  return snapshot;
}

/** Whole years, from a `YYYY-MM-DD` birthdate. `null` when it is not one. */
export function yearsOld(birthdate: string | null, now: Date = new Date()): number | null {
  if (!birthdate || !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) return null;
  const [y, m, d] = birthdate.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return null;
  let years = now.getUTCFullYear() - y;
  const month = now.getUTCMonth() + 1;
  const day = now.getUTCDate();
  if (month < m || (month === m && day < d)) years -= 1;
  return years;
}

/** True while the exclusion recorded in `snapshot` is still running. */
export function exclusionActive(
  exclusion: ProtectionSnapshot['exclusion'],
  now: number = Date.now(),
): boolean {
  if (!exclusion) return false;
  if (exclusion.permanent) return true;
  return exclusion.until !== null && Date.parse(exclusion.until) > now;
}

// --------------------------------------------------------------- protection

export function ProtectionTab({
  userId,
  profile,
}: {
  userId: number;
  profile: PlayerProfile;
}): ReactNode {
  const page = useAsync(() => fetchPlayerEvents(userId, SCAN_LIMIT), [userId]);

  if (page.error) {
    return (
      <ErrorState
        title="The protective state could not be reconstructed"
        detail={page.error}
        onRetry={page.reload}
      />
    );
  }
  if (page.loading && !page.data) return <TableSkeleton columns={['text', 'text']} rows={6} />;

  const events = page.data?.events ?? [];
  const snapshot = deriveProtection(events);
  const excluded = exclusionActive(snapshot.exclusion);
  const birthdate = profile.birthdate ?? snapshot.declared?.birthdate ?? null;
  const years = yearsOld(birthdate);
  const country = profile.countryCode ?? snapshot.declared?.countryCode ?? null;
  const pendingDue =
    snapshot.pending?.effectiveAt !== null &&
    snapshot.pending?.effectiveAt !== undefined &&
    Date.parse(snapshot.pending.effectiveAt) <= Date.now();
  const pendingSuperseded =
    snapshot.pending !== null &&
    snapshot.limits !== null &&
    snapshot.limits.eventId > snapshot.pending.eventId;

  return (
    <div className="stack">
      <div className="banner banner--slim">
        <span className="banner__icon">
          <IconShield />
        </span>
        <div className="banner__body">
          <span className="banner__title">Read-only, by design.</span>
          <span className="banner__text">
            A self-exclusion cannot be shortened and a deposit limit cannot be raised from here —
            the server has no code path that does either, so there is no control to offer. A
            decrease binds immediately; an increase always waits 24 hours.
          </span>
        </div>
      </div>

      {excluded ? (
        <div className="banner banner--danger">
          <span className="banner__icon">
            <IconAlert />
          </span>
          <div className="banner__body">
            <div className="banner__title">
              {snapshot.exclusion?.permanent
                ? 'Permanently self-excluded'
                : `Self-excluded until ${shortTime(snapshot.exclusion?.until ?? null)}`}
            </div>
            This account cannot bet or deposit. Starting an exclusion revoked every live session.
            Nothing in this panel can lift it.
          </div>
        </div>
      ) : null}

      <Card title="Identity and consent" icon={<IconShield />}>
        <div className="kv kv--spaced">
          <span className="kv__k">Declared country</span>
          <span className="kv__v">{country ?? '— (never declared)'}</span>
          <span className="kv__k">Birthdate</span>
          <span className="kv__v">{birthdate ?? '— (account predates the age gate)'}</span>
          <span className="kv__k">Age</span>
          <span className="kv__v">
            {years === null ? (
              '—'
            ) : years < MIN_AGE_YEARS ? (
              <>
                {years} <Badge tone="loss">under {MIN_AGE_YEARS}</Badge>
              </>
            ) : (
              <>
                {years} <Badge tone="win">of age</Badge>
              </>
            )}
          </span>
          <span className="kv__k">Terms accepted</span>
          <span className="kv__v">
            {snapshot.terms ? (
              <>
                <span className="mono-code">{snapshot.terms.version}</span>{' '}
                <span className="dim">
                  · {shortTime(snapshot.terms.at)}
                  {snapshot.terms.source ? ` · at ${snapshot.terms.source}` : ''}
                </span>
              </>
            ) : snapshot.declared?.termsVersion ? (
              <>
                <span className="mono-code">{snapshot.declared.termsVersion}</span>{' '}
                <span className="dim">· declared at sign-up</span>
              </>
            ) : (
              '— (no acceptance recorded in the visible history)'
            )}
          </span>
          <span className="kv__k">Marketing consent</span>
          <span className="kv__v">
            {profile.marketingOptIn === null
              ? '— (never asked)'
              : profile.marketingOptIn
                ? 'Opted in'
                : 'Opted out'}
          </span>
        </div>
      </Card>

      <Card title="Deposit limits" icon={<IconShield />}>
        {snapshot.limits ? (
          <>
            <LimitRows set={snapshot.limits.set} />
            <p className="field__hint field__hint--block">
              Last change {shortTime(snapshot.limits.at)} ({age(snapshot.limits.at)} ago)
              {snapshot.limits.windows.length > 0
                ? ` · ${snapshot.limits.windows.join(', ')} tightened`
                : ''}
              . A decrease binds the moment the player asks.
            </p>
          </>
        ) : (
          <EmptyState
            compact
            title="No limit change in the visible history"
            hint="That is not the same as “no limit”. The set in force is readable only by the player, at /me/limits; this section reconstructs it from recorded changes."
          />
        )}

        {snapshot.pending ? (
          <div className="subcard">
            <div className="row-actions row-actions--start">
              <Badge tone={pendingSuperseded ? 'neutral' : pendingDue ? 'win' : 'pending'}>
                {pendingSuperseded
                  ? 'superseded by a later change'
                  : pendingDue
                    ? 'due — in force by now'
                    : `lands ${shortTime(snapshot.pending.effectiveAt)}`}
              </Badge>
              <span className="field__hint">
                Requested increase · asked {age(snapshot.pending.at)} ago
                {snapshot.pending.windows.length > 0
                  ? ` · ${snapshot.pending.windows.join(', ')}`
                  : ''}
              </span>
            </div>
            <LimitRows set={snapshot.pending.set} />
            <p className="field__hint field__hint--block">
              Every increase waits 24 hours and is promoted by the server without an event of its
              own, so an increase whose time has passed reads as in force rather than pending.
            </p>
          </div>
        ) : null}
      </Card>

      <Card title="Self-exclusion" icon={<IconShield />}>
        {snapshot.exclusion ? (
          <div className="kv kv--spaced">
            <span className="kv__k">State</span>
            <span className="kv__v">
              {excluded ? (
                <Badge tone="loss">
                  {snapshot.exclusion.permanent ? 'permanent' : 'active'}
                </Badge>
              ) : (
                <Badge tone="neutral">expired</Badge>
              )}
            </span>
            <span className="kv__k">Ends</span>
            <span className="kv__v">
              {snapshot.exclusion.permanent
                ? 'Never — permanent'
                : snapshot.exclusion.until
                  ? `${shortTime(snapshot.exclusion.until)} (${age(snapshot.exclusion.until)})`
                  : '—'}
            </span>
            <span className="kv__k">Recorded</span>
            <span className="kv__v" title={absolute(snapshot.exclusion.at)}>
              {shortTime(snapshot.exclusion.at)}
              {snapshot.exclusion.extended ? ' · extended an existing exclusion' : ''}
            </span>
          </div>
        ) : (
          <EmptyState
            compact
            title="No self-exclusion recorded"
            hint="This player has never excluded themselves in the visible history."
          />
        )}
      </Card>

      {snapshot.truncated ? (
        <p className="field__hint field__hint--block">
          Reconstructed from the newest {SCAN_LIMIT} events, and that page is full — a change
          older than the oldest row on the Events tab is not visible here.
        </p>
      ) : null}
    </div>
  );
}

function LimitRows({ set }: { set: LimitTriple }): ReactNode {
  const rows: { label: string; value: string | null }[] = [
    { label: 'Daily', value: set.daily },
    { label: 'Weekly', value: set.weekly },
    { label: 'Monthly', value: set.monthly },
  ];
  return (
    <div className="kv kv--spaced">
      {rows.map((row) => (
        <div key={row.label} style={{ display: 'contents' }}>
          <span className="kv__k">{row.label}</span>
          <span className="kv__v">
            {row.value === null ? <span className="dim">no limit</span> : format(row.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// -------------------------------------------------------------- the history

/** What each event type is called in a sentence an operator can read aloud. */
const EVENT_LABEL: Record<string, string> = {
  login_succeeded: 'Signed in',
  login_failed: 'Sign-in failed',
  password_changed: 'Password changed',
  password_reset_requested: 'Password reset requested',
  password_reset_completed: 'Password reset completed',
  password_reset_issued: 'Reset link issued by an operator',
  withdrawal_destination_submitted: 'Payout destination submitted',
  self_exclusion_changed: 'Self-exclusion changed',
  limit_changed: 'Deposit limit changed',
  limit_increase_scheduled: 'Limit increase scheduled',
  terms_accepted: 'Terms accepted',
  registration_declared: 'Registration declared',
  consent_changed: 'Consent changed',
};

const EVENT_TONE: Record<string, 'win' | 'loss' | 'pending' | 'info' | 'accent' | 'neutral'> = {
  login_failed: 'loss',
  password_reset_issued: 'pending',
  password_reset_requested: 'pending',
  password_reset_completed: 'info',
  password_changed: 'info',
  self_exclusion_changed: 'loss',
  limit_changed: 'info',
  limit_increase_scheduled: 'pending',
  withdrawal_destination_submitted: 'accent',
};

/**
 * Defence in depth on the detail blob.
 *
 * The server writes ids, kinds, counts and MASKED fingerprints — never an
 * account number and never a token. This panel does not rely on that alone: any
 * key that names a raw credential or a raw destination is printed as `[hidden]`
 * rather than trusted, so a future event type that carries one by accident
 * cannot leak it onto an operator's screen or into a copied dispute record.
 */
const SENSITIVE = /token|secret|password|iban|vpa|accountnumber|account_number|routing|address|pan\b/i;

function detailPairs(detail: unknown): { key: string; value: string }[] {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return [];
  return Object.entries(detail as Record<string, unknown>).map(([key, value]) => {
    if (SENSITIVE.test(key)) return { key, value: '[hidden]' };
    if (value === null || value === undefined) return { key, value: '—' };
    if (typeof value === 'object') return { key, value: JSON.stringify(value) };
    return { key, value: String(value) };
  });
}

/** One event as a line of plain text — what gets copied for a dispute. */
export function eventLine(event: PlayerEventView): string {
  const pairs = detailPairs(event.detail)
    .map((p) => `${p.key}=${p.value}`)
    .join(' ');
  return [
    absolute(event.createdAt),
    EVENT_LABEL[event.type] ?? event.type,
    `actor=${event.actor}`,
    event.ip ? `ip=${event.ip}` : '',
    pairs,
  ]
    .filter((part) => part !== '')
    .join('  ·  ');
}

export function EventsTab({ userId }: { userId: number }): ReactNode {
  const first = useAsync(() => fetchPlayerEvents(userId, 50), [userId]);
  const [older, setOlder] = useState<PlayerEventView[]>([]);
  const [busy, setBusy] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const toast = useToast();

  // A different player is a different history — anything paged in for the last
  // one must go, or the two would render as one account's story.
  useEffect(() => {
    setOlder([]);
    setExhausted(false);
  }, [userId]);

  if (first.error) {
    return (
      <ErrorState title="The history did not load" detail={first.error} onRetry={first.reload} />
    );
  }
  if (first.loading && !first.data) {
    return <TableSkeleton columns={['text', 'text', 'text', 'text']} rows={8} />;
  }

  const rows = [...(first.data?.events ?? []), ...older];
  const counts = first.data?.counts ?? {};
  const totals = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  async function loadOlder(): Promise<void> {
    const last = rows[rows.length - 1];
    if (!last || busy) return;
    setBusy(true);
    try {
      const page = await fetchPlayerEvents(userId, 50, last.id);
      if (page.events.length === 0) setExhausted(true);
      setOlder((current) => [...current, ...page.events]);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not load older events.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function copyAll(): Promise<void> {
    const text = rows.map(eventLine).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast(`${rows.length} events copied.`);
    } catch {
      toast('The clipboard is not available here — select the table instead.', 'error');
    }
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        compact
        icon={<IconPulse size="lg" />}
        title="No recorded history"
        hint="Logins, password resets, payout destinations and consent changes appear here. An account that has done none of those since the log was built has nothing to show."
      />
    );
  }

  return (
    <div className="stack stack--tight">
      <div className="toolbar">
        <span className="toolbar__note">
          The same rows the player reads at /me/events — nothing hidden, nothing added. Newest
          first.
        </span>
        <span className="card__spacer" />
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => void copyAll()}>
          Copy {rows.length} rows
        </button>
      </div>

      {totals.length > 0 ? (
        <div className="row-actions row-actions--start">
          {totals.map(([type, n]) => (
            <span key={type} className="chip">
              {EVENT_LABEL[type] ?? type} · {n}
            </span>
          ))}
        </div>
      ) : null}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th>By</th>
              <th>Detail</th>
              <th>From</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event) => (
              <tr key={event.id}>
                <td className="nowrap muted" title={absolute(event.createdAt)}>
                  {shortTime(event.createdAt)}
                  <span className="cell-sub">{age(event.createdAt)} ago</span>
                </td>
                <td className="cell-lead nowrap">
                  <Badge tone={EVENT_TONE[event.type] ?? 'neutral'}>
                    {EVENT_LABEL[event.type] ?? event.type}
                  </Badge>
                </td>
                <td className="muted nowrap">{event.actor}</td>
                <td className="muted">
                  {detailPairs(event.detail).length === 0 ? (
                    '—'
                  ) : (
                    <span className="mono-code">
                      {detailPairs(event.detail)
                        .map((p) => `${p.key}=${p.value}`)
                        .join('  ')}
                    </span>
                  )}
                </td>
                <td className="muted nowrap" title={event.userAgent ?? ''}>
                  {event.ip ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row-actions row-actions--start">
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          disabled={busy || exhausted}
          onClick={() => void loadOlder()}
        >
          {exhausted ? 'That is the whole history' : busy ? 'Loading…' : 'Load older'}
        </button>
      </div>
    </div>
  );
}
